import { workspacePortal } from 'core-access';
import { HttpClient } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';

import { AuthService } from '../services/auth/authservice';
import {
  OrganizationContextItem,
  OrganizationContextResponse,
  OrganizationContextService,
} from './organization-context.service';
import { environment } from '../../../environments/environment';
import { isAuthExpired, redirectToLogin } from '../interceptors/auth-expired.interceptor';
import { workspaceCalendarAllowed, hasWorkspacePermission, hasWorkspaceRole, ownerOnlyAllowed } from '../access/workspace-access';

const STALE_ORG_STORAGE_KEYS = [
  'orgId',
  'organizationId',
  'selectedOrgId',
  'selectedOrganizationId',
  'workspaceOrgId',
  'activeOrgId',
];
const PLATFORM_SUPPORT_SESSION_STORAGE_KEY = 'platformSupportSessionId';

type WorkspaceRouteAccess = {
  pmWorkspaceOnly: boolean;
  ownerOnly: boolean;
  permissions: string[];
  roles: string[];
};

export const backofficeRoleGuard: CanActivateFn = async (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const http = inject(HttpClient);
  const auth = inject(AuthService);
  const organizationContextService = inject(OrganizationContextService);
  organizationContextService.clearOrganizationContextCache();
  try {
    rememberPlatformSupportSession(router, state.url);

    const session = await auth.ensureSession();

    if (!session?.authenticated) {
      redirectToLogin();
      return false;
    }

    const roles = session?.roles ?? [];
    const accountType = String(session?.accountType ?? '').toUpperCase();

    const platformAllowed = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN');
    const maintenanceAllowed = roles.includes('ROLE_MAINTENANCE');

    const accountTypeAllowed = accountType === 'LANDLORD' || accountType === 'PROPERTY_MANAGER';
    const backofficeRoleAllowed = roles.includes('ROLE_LANDLORD')
      || roles.includes('ROLE_PROPERTY_MANAGER')
      || roles.includes('ROLE_MANAGER');

    let context: OrganizationContextResponse | null = null;
    let contextLoaded = false;

    try {
      context = await firstValueFrom(
        organizationContextService.getOrganizationContext().pipe(timeout(8000)),
      );
      contextLoaded = true;
    } catch (e) {
      console.error('[backofficeRoleGuard] organization context failed:', e);

      if (isAuthExpired(e)) {
        try {
          await firstValueFrom(auth.refreshAccessToken().pipe(timeout(8000)));
          organizationContextService.clearOrganizationContextCache();
          context = await firstValueFrom(
            organizationContextService.getOrganizationContext().pipe(timeout(8000)),
          );
          contextLoaded = true;
        } catch (retryError) {
          console.error('[backofficeRoleGuard] organization context retry failed:', retryError);
          redirectToLogin();
          return false;
        }
      }
    }

    const requestedId = currentOrgId(router, state.url);
    const organizations = context?.organizations ?? [];
    const selected = organizations.find(org => org.portalSlug && org.portalSlug.toLowerCase() === workspaceSlugFromHost())
      ?? organizations.find(org => org.orgId === requestedId)
      ?? (organizations.length === 1 ? organizations[0] : null);
    if (selected && workspacePortal(selected) === 'MAINTENANCE') {
      const url = new URL('/dashboard', environment.maintenanceAppUrl);
      url.searchParams.set('orgId', selected.orgId);
      window.location.href = url.toString();
      return false;
    }
    if (selected && (selected.orgRole === 'TENANT_PORTAL' || selected.portalMode === 'TENANT')) {
      const url = new URL('/dashboard', environment.tenantAppUrl);
      url.searchParams.set('orgId', selected.orgId);
      window.location.href = url.toString();
      return false;
    }

    // A pending invitation must be reviewable before the first workspace is accepted.
    if (!organizations.length && state.url.split('?')[0] === '/dashboard') {
      const pending = await firstValueFrom(http.get<unknown[]>(
        `${environment.apiGatewayUrl}/property-service/api/v1/me/staff-invitations`, { withCredentials: true },
      ).pipe(timeout(8000))).catch(() => []);
      if (pending.length) return true;
    }

    const hasUsableOrganization = (context?.organizations ?? []).some((org) => workspaceIsUsable(org));
    const hasOrgAccess = !!context?.hasMembership && hasUsableOrganization;

    if (platformAllowed && !accountTypeAllowed && !hasOrgAccess) {
      window.location.href = platformAdminUrl(state.url);
      return false;
    }

    if (maintenanceAllowed && !accountTypeAllowed && !hasOrgAccess) {
      window.location.href = maintenancePortalUrl(state.url);
      return false;
    }

    if ((accountTypeAllowed || hasOrgAccess) && !contextLoaded) {
      return router.parseUrl('/session-expired');
    }

    if (accountTypeAllowed || hasOrgAccess) {
      const access = routeAccessFromData(route.data);
      const requestedUrl = router.parseUrl(state.url);
      if (state.url.split('?')[0] === '/properties/add' && requestedUrl.queryParams['mode'] === 'edit') {
        if (!requestedUrl.queryParams['propertyId']) return router.parseUrl('/properties');
        access.permissions = [requestedUrl.queryParams['intent'] === 'add-unit' ? 'UNIT_CREATE' : 'PROPERTY_UPDATE'];
      }
      const result = resolveWorkspaceAccess(router, state.url, context, access);
      organizationContextService.rememberSelectedOrganization(organizationContextService.selectedOrganizationId());
      return result;
    }

    return router.parseUrl('/session-expired');
  } catch (e) {
    console.error('[backofficeRoleGuard] browser error:', e);
    redirectToLogin();
    return false;
  }
};

function resolveWorkspaceAccess(
  router: Router,
  stateUrl: string,
  context: OrganizationContextResponse | null,
  routeAccess: WorkspaceRouteAccess = emptyRouteAccess(),
) {
  const organizations = context?.organizations ?? [];
  const usableOrganizations = organizations.filter((org) => workspaceIsUsable(org));
  const hostSlug = workspaceSlugFromHost();
  const requestedOrgId = currentOrgId(router, stateUrl);

  if (hostSlug) {
    const hostOrg = organizations.find((org) => org.portalSlug?.toLowerCase() === hostSlug);
    if (!hostOrg) {
      redirectToPublic('/login');
      return false;
    }

    if (workspaceIsSuspended(hostOrg)) {
      return router.parseUrl(suspendedWorkspaceUrl(hostOrg));
    }

    // A workspace whose subscription has ended stays open in view-only mode (the shell shows a renew banner and
    // the server refuses changes), so its team can still see and export their data.

    if (isOwnerPortalWorkspace(hostOrg)) {
      window.location.href = ownerPortalUrl(hostOrg, stateUrl);
      return false;
    }

    if (requestedOrgId !== hostOrg.orgId) {
      const nextUrl = withOrgId(router, stateUrl, hostOrg);
      rememberOrg(hostOrg.orgId);
      return router.parseUrl(nextUrl);
    }

    return allowedWorkspaceResult(router, stateUrl, hostOrg, routeAccess);
  }

  if (isGenericHostedBackoffice()) {
    const requestedOrg = requestedOrgId
      ? usableOrganizations.find((org) => org.orgId === requestedOrgId)
      : null;

    if (requestedOrg && isOwnerPortalWorkspace(requestedOrg)) {
      window.location.href = ownerPortalUrl(requestedOrg, stateUrl);
      return false;
    }

    if (requestedOrg?.portalSlug) {
      if (!workspaceAllowsRoute(requestedOrg, routeAccess)) {
        window.location.href = brandedBackofficeUrl(requestedOrg, workspaceLandingPath(requestedOrg));
        return false;
      }
      rememberOrg(requestedOrg.orgId);
      window.location.href = brandedBackofficeUrl(requestedOrg, stateUrl);
      return false;
    }

    if (requestedOrg) {
      return allowedWorkspaceResult(router, stateUrl, requestedOrg, routeAccess);
    }

    if (usableOrganizations.length === 1) {
      const onlyOrg = usableOrganizations[0];
      rememberOrg(onlyOrg.orgId);
      if (isOwnerPortalWorkspace(onlyOrg)) {
        window.location.href = ownerPortalUrl(onlyOrg, stateUrl);
        return false;
      }
      if (onlyOrg.portalSlug) {
        window.location.href = brandedBackofficeUrl(
          onlyOrg,
          workspaceAllowsRoute(onlyOrg, routeAccess) ? stateUrl : workspaceLandingPath(onlyOrg),
        );
        return false;
      }
      if (!workspaceAllowsRoute(onlyOrg, routeAccess)) {
        return router.parseUrl(withOrgId(router, workspaceLandingPath(onlyOrg), onlyOrg));
      }
      return router.parseUrl(withOrgId(router, stateUrl, onlyOrg));
    }

    forgetOrg();
    redirectToPublic('/select-workspace');
    return false;
  }

  if (requestedOrgId) {
    const requestedOrg = organizations.find((org) => org.orgId === requestedOrgId);

    if (requestedOrg && workspaceIsUsable(requestedOrg)) {
      return allowedWorkspaceResult(router, stateUrl, requestedOrg, routeAccess);
    }

    if (!organizations.length) {
      // A query parameter is not evidence of membership. Never open a protected
      // workspace while its organization context is empty or has been revoked.
      forgetOrg();
      return router.parseUrl('/session-expired');
    }

    forgetOrg();

    if (requestedOrg) {
      if (workspaceIsSuspended(requestedOrg)) {
        return router.parseUrl(suspendedWorkspaceUrl(requestedOrg));
      }

      if (!canManageWorkspaceSubscription(requestedOrg)) {
        redirectToPublic('/select-workspace');
        return false;
      }

      redirectToPublic(`/subscription/select?orgId=${encodeURIComponent(requestedOrg.orgId)}`);
      return false;
    }

    redirectToPublic(organizations.length > 0 ? '/select-workspace' : '/organization/create');
    return false;
  }

  forgetOrg();

  if (usableOrganizations.length > 1) {
    redirectToPublic('/select-workspace');
    return false;
  }

  if (usableOrganizations.length === 1) {
    if (isOwnerPortalWorkspace(usableOrganizations[0])) {
      rememberOrg(usableOrganizations[0].orgId);
      window.location.href = ownerPortalUrl(usableOrganizations[0], stateUrl);
      return false;
    }
    if (!workspaceAllowsRoute(usableOrganizations[0], routeAccess)) {
      rememberOrg(usableOrganizations[0].orgId);
      return router.parseUrl(withOrgId(router, workspaceLandingPath(usableOrganizations[0]), usableOrganizations[0]));
    }
    const nextUrl = withOrgId(router, stateUrl, usableOrganizations[0]);
    rememberOrg(usableOrganizations[0].orgId);
    return router.parseUrl(nextUrl);
  }

  if (organizations.length > 0) {
    const suspendedOrganizations = organizations.filter((org) => workspaceIsSuspended(org));
    if (suspendedOrganizations.length === organizations.length && suspendedOrganizations[0]) {
      return router.parseUrl(suspendedWorkspaceUrl(suspendedOrganizations[0]));
    }
    redirectToPublic('/select-workspace');
    return false;
  }

  redirectToPublic('/organization/create');
  return false;
}

function canManageWorkspaceSubscription(organization: { owner?: boolean; orgRole?: string | null }): boolean {
  const role = String(organization?.orgRole ?? '').trim().toUpperCase();
  return Boolean(organization?.owner) || role === 'OWNER' || role === 'ADMIN' || role === 'PLATFORM_SUPPORT';
}

function workspaceStatus(organization: OrganizationContextItem | null | undefined): string {
  return String(organization?.status ?? 'ACTIVE').trim().toUpperCase();
}

function workspaceIsActive(organization: OrganizationContextItem | null | undefined): boolean {
  return workspaceStatus(organization) === 'ACTIVE';
}

function workspaceIsSuspended(organization: OrganizationContextItem | null | undefined): boolean {
  return workspaceStatus(organization) === 'SUSPENDED';
}

/** Suspended or archived workspaces can't be opened; one whose subscription has ended opens view only. */
function workspaceIsUsable(organization: OrganizationContextItem | null | undefined): boolean {
  return workspaceIsActive(organization);
}

function suspendedWorkspaceUrl(organization: OrganizationContextItem): string {
  const params = new URLSearchParams();
  params.set('orgId', organization.orgId);
  if (organization.orgName) {
    params.set('name', organization.orgName);
  }
  return `/workspace-suspended?${params.toString()}`;
}

function allowedWorkspaceResult(
  router: Router,
  stateUrl: string,
  organization: OrganizationContextItem,
  routeAccess: WorkspaceRouteAccess,
) {
  rememberOrg(organization.orgId);

  if (isOwnerPortalWorkspace(organization)) {
    window.location.href = ownerPortalUrl(organization, stateUrl);
    return false;
  }

  if (workspaceAllowsRoute(organization, routeAccess)) {
    return true;
  }

  return router.parseUrl(withOrgId(router, workspaceLandingPath(organization), organization));
}

function workspaceAllowsRoute(
  organization: OrganizationContextItem | null | undefined,
  routeAccess: WorkspaceRouteAccess,
): boolean {
  if (!organization) {
    return false;
  }

  if (routeAccess.pmWorkspaceOnly && !isPropertyManagementWorkspace(organization)) {
    return false;
  }

  if (routeAccess.permissions.includes('CALENDAR_VIEW') && !workspaceCalendarAllowed(organization)) return false;

  if (routeAccess.ownerOnly && !ownerOnlyAllowed(organization, routeAccess.permissions)) {
    return false;
  }

  if (routeAccess.permissions.length && !hasWorkspacePermission(organization, ...routeAccess.permissions)) {
    return false;
  }

  if (routeAccess.roles.length && !hasWorkspaceRole(organization, routeAccess.roles)) {
    return false;
  }

  return true;
}

function routeAccessFromData(data: Record<string, unknown>): WorkspaceRouteAccess {
  return {
    pmWorkspaceOnly: data?.['pmWorkspaceOnly'] === true,
    ownerOnly: data?.['ownerOnly'] === true,
    permissions: toStringList(data?.['permissions']),
    roles: toStringList(data?.['roles']),
  };
}

function emptyRouteAccess(): WorkspaceRouteAccess {
  return {
    pmWorkspaceOnly: false,
    ownerOnly: false,
    permissions: [],
    roles: [],
  };
}

function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  }

  const text = String(value ?? '').trim();
  return text ? [text] : [];
}

function isPropertyManagementWorkspace(organization: OrganizationContextItem | null | undefined): boolean {
  const orgType = String(organization?.orgType ?? '').toUpperCase();
  const portalMode = String(organization?.portalMode ?? '').toUpperCase();
  return portalMode === 'PROPERTY_MANAGER'
    || orgType === 'PROPERTY_MANAGER'
    || orgType === 'PROPERTY_MANAGEMENT';
}

function isOwnerPortalWorkspace(organization: OrganizationContextItem | null | undefined): boolean {
  const role = String(organization?.orgRole ?? '').toUpperCase();
  const portalMode = String(organization?.portalMode ?? '').toUpperCase();
  return role === 'LANDLORD_PORTAL' || portalMode === 'OWNER_PORTAL';
}

function workspaceLandingPath(organization: OrganizationContextItem): string {
  const path = normalizedPath(organization.landingPath || '/dashboard');
  return path || '/dashboard';
}

function ownerPortalUrl(organization: OrganizationContextItem, stateUrl: string): string {
  const url = new URL(environment.ownerAppUrl);
  const slug = organization.portalSlug?.trim().toLowerCase();
  if (slug && !isLocalHost(url.hostname) && !url.hostname.startsWith(`${slug}.`)) {
    url.hostname = `${slug}.${url.hostname}`;
  }

  url.pathname = normalizedPath(stateUrl) === '/settings' ? '/settings' : '/dashboard';
  url.searchParams.set('orgId', organization.orgId);
  return url.toString();
}

function platformAdminUrl(stateUrl: string): string {
  const url = new URL(environment.platformAdminAppUrl);
  const path = normalizedPath(stateUrl);
  url.pathname = path === '/settings' ? '/settings' : '/dashboard';
  return url.toString();
}

function maintenancePortalUrl(stateUrl: string): string {
  const url = new URL(environment.maintenanceAppUrl);
  const path = normalizedPath(stateUrl);
  const allowedPaths = new Set([
    '/dashboard',
    '/work-orders',
    '/schedule',
    '/inspections',
    '/parts',
    '/reports',
    '/settings',
  ]);
  url.pathname = allowedPaths.has(path) ? path : '/dashboard';
  return url.toString();
}

function tenantPortalUrl(stateUrl: string): string {
  const url = new URL(environment.tenantAppUrl);
  const path = normalizedPath(stateUrl);
  const allowedPaths = new Set([
    '/dashboard',
    '/rents-payment',
    '/maintenance',
    '/documents',
    '/leases',
    '/inspections',
    '/calendar',
    '/settings',
    '/profile',
  ]);
  url.pathname = allowedPaths.has(path) ? path : '/dashboard';
  return url.toString();
}

function isTenantOnlySession(roles: string[], accountType: string): boolean {
  if (accountType !== 'TENANT' && !roles.includes('ROLE_TENANT')) {
    return false;
  }

  return !roles.some((role) =>
    role === 'ROLE_LANDLORD'
    || role === 'ROLE_PROPERTY_MANAGER'
    || role === 'ROLE_MANAGER'
    || role === 'ROLE_ADMIN'
    || role === 'ROLE_SUPER_ADMIN'
    || role === 'ROLE_MAINTENANCE',
  );
}

function normalizedPath(value: string | null | undefined): string {
  const path = String(value ?? '').split('?')[0].split('#')[0].trim();
  if (!path) {
    return '';
  }
  return path.startsWith('/') ? path : `/${path}`;
}

function currentOrgId(router: Router, stateUrl: string): string | null {
  const tree = router.parseUrl(stateUrl);
  const orgId = tree.queryParams['orgId'];
  if (typeof orgId === 'string' && orgId.trim()) {
    return orgId;
  }

  try {
    const storedOrgId = window.sessionStorage.getItem('selectedOrgId');
    return storedOrgId?.trim() || null;
  } catch {
    return null;
  }
}

function rememberPlatformSupportSession(router: Router, stateUrl: string): void {
  const tree = router.parseUrl(stateUrl);
  const supportSessionId = tree.queryParams['supportSessionId'];
  if (typeof supportSessionId !== 'string' || !supportSessionId.trim()) {
    return;
  }

  try {
    window.sessionStorage.setItem(PLATFORM_SUPPORT_SESSION_STORAGE_KEY, supportSessionId.trim());
  } catch {
    // Session storage is best-effort only.
  }
}

function withOrgId(router: Router, stateUrl: string, organization: OrganizationContextItem): string {
  const tree = router.parseUrl(stateUrl);
  tree.queryParams = {
    ...tree.queryParams,
    orgId: organization.orgId,
  };
  return router.serializeUrl(tree);
}

function redirectToPublic(path: string): void {
  window.location.href = `${environment.publicAppUrl}${path}`;
}

function workspaceSlugFromHost(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const host = window.location.hostname.toLowerCase();
  if (isLocalHost(host)) {
    return null;
  }

  const baseHost = baseHostname(environment.backofficeAppUrl);
  if (!baseHost || host === baseHost || !host.endsWith(`.${baseHost}`)) {
    return null;
  }

  return host.slice(0, -(baseHost.length + 1)).split('.').filter(Boolean).at(-1) || null;
}

function isGenericHostedBackoffice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname.toLowerCase();
  if (isLocalHost(host)) {
    return false;
  }

  return host === baseHostname(environment.backofficeAppUrl);
}

function brandedBackofficeUrl(organization: OrganizationContextItem, stateUrl: string): string {
  const url = new URL(environment.backofficeAppUrl);
  const slug = organization.portalSlug?.trim().toLowerCase();
  if (slug && !url.hostname.startsWith(`${slug}.`)) {
    url.hostname = `${slug}.${url.hostname}`;
  }

  const state = new URL(stateUrl, url.origin);
  url.pathname = state.pathname;
  url.search = state.search;
  url.hash = state.hash;
  url.searchParams.set('orgId', organization.orgId);
  return url.toString();
}

function baseHostname(value: string): string | null {
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isLocalHost(host: string): boolean {
  return host === 'localhost' || host.includes('localhost') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
}

function rememberOrg(orgId: string): void {
  try {
    window.sessionStorage.setItem('selectedOrgId', orgId);
  } catch {
    // Storage is best-effort only.
  }
}

function forgetOrg(): void {
  for (const key of STALE_ORG_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    } catch {
      // Storage is best-effort only.
    }
  }
}
